<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { usersSessions } from '@consumer/api'
import { asyncOpenModal } from '@consumer/services/modals'
import { useTemplateRefsList } from '@vueuse/core'
import type GTextInput from '@consumer/components/GTextInput.vue'
import type { InputValidation } from '@shared/validation'
import { useSharedData } from '@consumer/composables/sharedData'
import { showNotice } from '@consumer/services/flash'
import { isTest } from '@shared/environment'

withDefaults(defineProps<{
  referer?: string
  authenticationError?: string
  errors?: any
  confirmSignup?: boolean
  modalAuth?: boolean
  submitLabel?: string
}>(), { submitLabel: 'Log In', })

const credentials = (defineModel<{
  email?: string
  password?: string
  code?: string
  recaptcha?: string
  hasPassword: boolean
  usePassword: boolean
  checkout?: boolean
}>('credentials', { required: true }))

const emit = defineEmits<{
  openForgotPassword: []
  success: [response: any]
  error: [error: any]
}>()

const props = useSharedData()

let wardenError = _ref(__props.authenticationError)
watch(() => __props.authenticationError, (message) => {
  if (message) {
    wardenError.value = message
  }
  // Ensure same message is detected as change and displayed each time.
  (props as any).authenticationError = undefined
})

// NOTE: Necessary only because warden sets errors in flash[:alert].
const passwordIsValid: InputValidation = {
  isValueValid: () => !wardenError.value,
  errorMessage: () => wardenError.value!,
  immediate: true,
}

async function onSubmit () {
  try {
    const user = {
      email: credentials.value.email,
      password: credentials.value.password,
      code: credentials.value.code,
      recaptcha: credentials.value.recaptcha,
    }
    if (!credentials.value.usePassword || !credentials.value.hasPassword) {
      user.code = codeCharacters.value.join('')
      delete user.password
    }
    else {
      delete user.code
    }
    const response = await usersSessions.create({
      data: { user, referer: __props.referer, modalAuth: __props.modalAuth, confirmSignup: __props.confirmSignup, checkout: credentials.value.checkout },
      visit: !__props.modalAuth,
    })
    emit('success', response)
  }
  catch (error: any) {
    emit('error', error)
    const errorMessage = error.response?.data?.error || error.message
    if (errorMessage) wardenError.value = errorMessage
  }
}

function openForgotPassword () {
  emit('openForgotPassword')
  return asyncOpenModal(import('@consumer/modals/ForgotPasswordModal.vue'), {
    email: credentials.value.email,
  })
}

const codeCharacters = ref(['', '', '', '', '', ''])
onMounted(() => {
  Array.from(credentials.value.code ?? '').forEach((char, index) => {
    codeCharacters.value[index] = char
  })
})
const inputRefs = useTemplateRefsList<InstanceType<typeof GTextInput>>()
const submitButtonRef = ref<HTMLElement>()

function loginWithPassword () {
  credentials.value.usePassword = true
  wardenError.value = undefined
}

function loginWithToken () {
  usersSessions.sendLoginLink({ data: { email: credentials.value.email } }).then(() => {
    credentials.value.usePassword = false
    wardenError.value = undefined
  })
}

async function resendCode () {
  const { email } = credentials.value
  await usersSessions.sendLoginLink({ data: { email } })
  showNotice('Check your email for a verification code.')
}

function onCodePaste (index: number, e: ClipboardEvent) {
  const pasteData = e.clipboardData?.getData('text')

  if (pasteData && pasteData.match(/\d{6}/)) {
    codeCharacters.value = pasteData.trim().split('')
    submitButtonRef.value?.focus()
    submitButtonRef.value?.click()
  }
}

function onCodeInput (index: number, e: InputEvent) {
  const key = e.data
  const nextInputRef = inputRefs.value[index + 1]

  if (key && key.match(/\d/) && nextInputRef && index < codeCharacters.value.length - 1) {
    nextInputRef.focus()
  }
}

function onCodeKeyup (index: number, event: KeyboardEvent) {
  const previusInputRef = inputRefs.value[index - 1]
  const key = event.key

  if (key === 'Delete') {
    codeCharacters.value[index] = ''
  }
  else if (key === 'Backspace' && previusInputRef) {
    event.preventDefault()
    previusInputRef.focus()
    codeCharacters.value[index - 1] = ''
  }
}

const showPasswordInput = _computed(() => !__props.confirmSignup && credentials.value.usePassword && credentials.value.hasPassword)
</script>

<template>
  <GForm :model="credentials" class="w-full" @submit="onSubmit">
    <div v-if="showPasswordInput">
      <GPasswordInput
        v-autofocus
        :label="false"
        :validate="passwordIsValid"
        autocomplete="current-password"
        @update:modelValue="wardenError = undefined"
      />
      <div class="forgot-password flex items-baseline justify-start mt-4 text-sm">
        Forgot password?
        &nbsp;
        <GButton
          primary
          flat
          type="button"
          label="Reset"
          @click="openForgotPassword"
        />
        &nbsp;or&nbsp;
        <GPromiseButton
          type="button"
          primary
          flat
          label="send code"
          @click="loginWithToken"
        />
      </div>
    </div>

    <div v-if="!showPasswordInput">
      <div class="grid items-center form-input-group code-characters">
        <GTextInput
          v-for="(inputCode, i) in codeCharacters"
          :key="i"
          :ref="inputRefs.set"
          v-model="codeCharacters[i]"
          :name="`code[${i}]`"
          :hasErrors="Boolean(wardenError)"
          autocapitalize
          :inputSize="1"
          mask="#"
          pattern="\d*"
          @focus="wardenError = undefined"
          @paste="onCodePaste(i, $event)"
          @input="onCodeInput(i, $event)"
          @keyup="onCodeKeyup(i, $event)"
        />
      </div>
      <GInputError :error="wardenError"/>

      <div class="flex items-baseline justify-start mt-4 text-sm">
        <GPromiseButton flat primary label="Resend code" @click="resendCode"/>
        <template v-if="credentials.hasPassword">
          &nbsp;or&nbsp;
          <GButton
            primary
            flat
            type="button"
            label="login with password"
            @click="loginWithPassword"
          />
        </template>
      </div>
    </div>

    <GSaveButton v-if="isTest" class="mt-6 w-full" name="login-submit-password" :label="submitLabel"/>
    <GRecaptcha v-else>
      <GSaveButton ref="submitButtonRef" class="mt-6 w-full" name="login-submit-password" :label="submitLabel"/>
    </GRecaptcha>
  </GForm>
</template>

<style lang="scss">
.form-input-group.code-characters {
  gap: 3%;
  grid-template-columns: repeat(6, minmax(36px, 56px));

  .g-input-field,
  .g-input,
  .g-inner-input {
    border-radius: 14%;
  }

  .g-inner-input {
    aspect-ratio: 1.1 / 1;
    font-size: 120%;
    padding: 0;
    text-align: center;

    @media screen and (min-width: 1024px) {
      font-size: 150%;
    }
  }
}
</style>
